import styled from '@emotion/styled';
import Box from '@mui/material/Box';
import { theme } from '@ui/theme/cobrainer-theme';
import { Fragment, Suspense, useContext } from 'react';
import { Outlet } from 'react-router';

import { AppBar } from './app-bar/AppBar';
import { NavigationMenuProvider } from './app-bar/menu-bar-v2/NavigationMenuContext';
import { shouldRenderOnboarding } from './app-base-conditions';
import { ConsentDialog } from './consent-dialog/ConsentDialog';
import { PageErrorBoundary } from './error-handling/ErrorBoundaries';
import { AppDrawers } from './pages/app-drawers';
import { route as onboardingRoutes } from './pages/onboarding/constants';
import { OnboardingForwarder } from './pages/onboarding/OnboardingForwarder';
import { ProfilePreview } from './pages/profile/profile-preview';
import { ApplicationContext } from '../../../shared/source/context/context';
import { useConsentQuery } from '../../../shared/source/query-hooks/consent';
import { PageLoadingSpinner } from '../design/components/LoadingSpinner';
import { SideDrawerNavigationV2 } from './app-bar/menu-bar-v2/NavigationMenuV2';

import type { FunctionComponent } from 'react';

const LayoutGrid = styled.div({
    backgroundColor: theme.colors.gray[200],
    display: 'grid',
    gridTemplateColumns: 'auto 1fr',
});

const MainContent = styled.div({
    backgroundColor: theme.colors.white[200],
    borderRadius: theme.borderRadius.m,
    marginBottom: theme.spacing.m,
    marginRight: theme.spacing.m,
    minWidth: 0,
    overflow: 'hidden',
});

export const Layout: FunctionComponent = () => {
    const { applicationState } = useContext(ApplicationContext);
    const { myUserProfile } = applicationState;
    const { data: consentData } = useConsentQuery();
    const { pathname } = applicationState.location;
    const { featureFlagService } = applicationState;

    const newMenuBar = featureFlagService.isFeatureFlagEnabled('new-menu-bar');

    if (myUserProfile.isNothing) {
        return <PageLoadingSpinner />;
    }

    if (
        shouldRenderOnboarding({
            consentStatus: consentData?.status,
            onboardingRoutesPath: onboardingRoutes.patterns.onboarding,
            pathname,
            userProfileExists: myUserProfile.value.exists === true,
        })
    ) {
        return (
            <Fragment>
                <OnboardingForwarder />
                <ConsentDialog />
            </Fragment>
        );
    }

    return (
        <NavigationMenuProvider>
            <Fragment>
                <AppBar />
                <Box component="main" flex={1} sx={{ position: 'relative' }}>
                    <PageErrorBoundary>
                        <Suspense fallback={<PageLoadingSpinner />}>
                            {newMenuBar ? (
                                <LayoutGrid>
                                    <SideDrawerNavigationV2 />
                                    <MainContent>
                                        <Outlet />
                                    </MainContent>
                                </LayoutGrid>
                            ) : (
                                <Outlet />
                            )}
                        </Suspense>
                        <AppDrawers />
                        <ProfilePreview />
                    </PageErrorBoundary>
                </Box>
            </Fragment>
        </NavigationMenuProvider>
    );
};
